import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface OpenMatch {
  id: string;
  club_id?: string | null;
  group_id?: string | null;
  is_open?: boolean;
  club?: {
    id: string;
    club_name: string;
    street_address?: string;
  } | null;
  match_date: string;
  status: string;
  published_by: string;
  team1_player1?: string;
  team1_player2?: string;
  team2_player1?: string;
  team2_player2?: string;
  created_at: string;
  publisher_profile?: {
    full_name: string;
    avatar_url?: string;
  };
  team1_player1_profile?: {
    full_name: string;
    avatar_url?: string;
    skill_rating: number;
  };
  team1_player2_profile?: {
    full_name: string;
    avatar_url?: string;
    skill_rating: number;
  };
  team2_player1_profile?: {
    full_name: string;
    avatar_url?: string;
    skill_rating: number;
  };
  team2_player2_profile?: {
    full_name: string;
    avatar_url?: string;
    skill_rating: number;
  };
  available_spots: number;
  team1_spots: number;
  team2_spots: number;
}

export const useOpenMatches = (userLocation?: string) => {
  const { session, ready } = useAuth();
  
  return useQuery({
    queryKey: ['open-matches', userLocation, session?.user?.id],
    queryFn: async () => {
      // Add grace period for time filtering (5 minutes)
      const gracePeriod = 5 * 60 * 1000; // 5 minutes in milliseconds
      const cutoffTime = new Date(Date.now() - gracePeriod).toISOString();
      
      const { data, error } = await supabase
        .from('published_matches')
        .select(`
          *,
          club:padel_clubs(id, club_name, street_address),
          publisher_profile:profiles!published_by(
            full_name,
            avatar_url
          ),
          team1_player1_profile:profiles!team1_player1(
            full_name,
            avatar_url,
            skill_rating
          ),
          team1_player2_profile:profiles!team1_player2(
            full_name,
            avatar_url,
            skill_rating
          ),
          team2_player1_profile:profiles!team2_player1(
            full_name,
            avatar_url,
            skill_rating
          ),
          team2_player2_profile:profiles!team2_player2(
            full_name,
            avatar_url,
            skill_rating
          )
        `)
        .eq('is_open', true)
        .eq('status', 'scheduled')
        .gt('match_date', cutoffTime)
        .order('match_date', { ascending: true });

      if (error) {
        console.error('❌ Error fetching open matches:', error);
        throw error;
      }

      // Calculate available spots for each match
      const matchesWithSpots = data?.map((match) => {
        const team1FilledSpots = [match.team1_player1, match.team1_player2].filter(Boolean).length;
        const team2FilledSpots = [match.team2_player1, match.team2_player2].filter(Boolean).length;
        const totalFilledSpots = team1FilledSpots + team2FilledSpots;
        
        return {
          ...match,
          available_spots: 4 - totalFilledSpots,
          team1_spots: 2 - team1FilledSpots,
          team2_spots: 2 - team2FilledSpots
        };
      }) || [];

      return matchesWithSpots as OpenMatch[];
    },
    enabled: ready, // Wait for auth to be ready
    staleTime: 2 * 60 * 1000, // 2 minutes — prevents refetch on every tab switch
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
